/* eslint-disable @typescript-eslint/no-unused-vars */
import { ElementRef, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, fromEvent, Subscription, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import {
  calculateAutoScrollState,
  getScrollState,
  isShowScrollNeeded,
  scrollToBottom,
} from '../../../utils/scroll.utils';
import { ChatScrollState, IChatScrollService } from '../interfaces/chat-scroll-service.interface';

@Injectable({
  providedIn: 'root',
})
export class ChatScrollService implements IChatScrollService, OnDestroy {
  // State management
  private readonly scrollStateSubject = new BehaviorSubject<ChatScrollState>({
    showScrollButton: false,
    autoScroll: true,
    lastScrollTop: 0,
    scrolledUp: false,
    scrolledDown: false,
    distanceFromBottom: 0,
    isAtBottom: true,
    isNearBottom: true,
  });

  // Auto-hide timer management
  private autoHideTimer: Subscription | null = null;
  private isInitialLoad = true;
  private hasUserInteracted = false;
  private readonly AUTO_HIDE_DELAY = 2000; // 3 seconds
  private readonly INITIAL_LOAD_DELAY = 1000; // 1 second delay for initial load

  // Public observables
  public readonly scrollState$ = this.scrollStateSubject.asObservable();
  public readonly showScrollButton$ = this.scrollState$.pipe(
    map((state) => state.showScrollButton),
    distinctUntilChanged(),
  );
  public readonly autoScroll$ = this.scrollState$.pipe(
    map((state) => state.autoScroll),
    distinctUntilChanged(),
  );
  public readonly isAtBottom$ = this.scrollState$.pipe(
    map((state) => state.isAtBottom),
    distinctUntilChanged(),
  );

  // Scroll container reference (using WeakMap to prevent memory leaks)
  private scrollContainer: ElementRef<HTMLElement> | null = null;
  private scrollSubscription: Subscription | null = null;
  private isProgrammaticScroll = false;
  private readonly elementRefMap = new WeakMap<HTMLElement, ElementRef<HTMLElement>>();

  /**
   * Initializes scroll monitoring for a container element
   */
  public initScrollMonitoring(container: ElementRef<HTMLElement>): void {
    this.scrollContainer = container;
    this.isInitialLoad = true;
    this.hasUserInteracted = false;

    // Store reference in WeakMap for safe access
    this.elementRefMap.set(container.nativeElement, container);

    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
    }

    // Create scroll observable
    const scroll$ = fromEvent(container.nativeElement, 'scroll').pipe(
      startWith(null),
      // Filter out programmatic scrolls
      filter(() => !this.isProgrammaticScroll),
      debounceTime(50), // Reduced debounce for more responsive scroll detection
    );

    // Monitor scroll changes
    this.scrollSubscription = scroll$.subscribe(() => {
      // Mark that user has interacted when they scroll
      if (!this.isProgrammaticScroll) {
        this.hasUserInteracted = true;
      }
      this.updateScrollState();
    });

    // Initial state update with longer delay to prevent premature button display
    setTimeout(() => {
      this.isInitialLoad = false;
      this.updateScrollState();
    }, this.INITIAL_LOAD_DELAY);
  }

  /**
   * Updates the scroll state based on current scroll position
   */
  private updateScrollState(): void {
    if (!this.scrollContainer || !this.scrollContainer.nativeElement) return;

    const element = this.scrollContainer.nativeElement;
    const currentState = this.scrollStateSubject.value;
    const scrollState = getScrollState(element, currentState.lastScrollTop);

    // Calculate auto-scroll state only if this is a user scroll
    let autoScroll = currentState.autoScroll;
    if (!this.isProgrammaticScroll) {
      autoScroll = calculateAutoScrollState(scrollState, currentState.autoScroll);
    }

    // Determine if scroll button should be shown
    // Only show if: not initial load AND user has interacted AND scroll is needed
    const shouldShowBasedOnScroll = !this.isInitialLoad && this.hasUserInteracted && isShowScrollNeeded(element);

    // Handle auto-hide timer logic
    this.handleAutoHideTimer(shouldShowBasedOnScroll, currentState.showScrollButton);

    // Update state
    const newState = {
      showScrollButton: shouldShowBasedOnScroll,
      autoScroll,
      lastScrollTop: scrollState.scrollTop,
      scrolledUp: scrollState.isScrollingUp,
      scrolledDown: scrollState.isScrollingDown,
      distanceFromBottom: scrollState.distanceFromBottom,
      isAtBottom: scrollState.distanceFromBottom <= 50,
      isNearBottom: scrollState.distanceFromBottom < 200,
    };

    // Debug log when auto-scroll changes
    // if (currentState.autoScroll !== autoScroll) {
    //   console.debug('Auto-scroll changed:', {
    //     from: currentState.autoScroll,
    //     to: autoScroll,
    //     distanceFromBottom: scrollState.distanceFromBottom,
    //     isScrollingUp: scrollState.isScrollingUp,
    //     isProgrammatic: this.isProgrammaticScroll,
    //   });
    // }

    this.scrollStateSubject.next(newState);
  }

  /**
   * Handles the auto-hide timer logic for the scroll button
   */
  private handleAutoHideTimer(shouldShowBasedOnScroll: boolean, currentlyShowing: boolean): void {
    // Clear existing timer if any
    if (this.autoHideTimer) {
      this.autoHideTimer.unsubscribe();
      this.autoHideTimer = null;
    }

    // If the button should be shown and is either newly shown or user is scrolling
    if (shouldShowBasedOnScroll) {
      // Start auto-hide timer
      this.autoHideTimer = timer(this.AUTO_HIDE_DELAY).subscribe(() => {
        // Hide the button after the delay
        const currentState = this.scrollStateSubject.value;
        this.scrollStateSubject.next({
          ...currentState,
          showScrollButton: false,
        });

        // Clear the timer reference
        this.autoHideTimer = null;
      });
    }
  }

  /**
   * Scrolls to the bottom of the container
   */
  public scrollToBottom(smooth = true): void {
    if (!this.scrollContainer || !this.scrollContainer.nativeElement) return;

    this.isProgrammaticScroll = true;
    scrollToBottom(this.scrollContainer.nativeElement, smooth);

    // Reset flag after scroll animation completes
    setTimeout(() => {
      this.isProgrammaticScroll = false;
    }, 300);

    // Update state to reflect we're at bottom but don't change autoScroll
    // This is a programmatic scroll and shouldn't affect the user's auto-scroll preference
    this.scrollStateSubject.next({
      ...this.scrollStateSubject.value,
      isAtBottom: true,
      isNearBottom: true,
      distanceFromBottom: 0,
    });
  }

  /**
   * Scrolls to the bottom with a delay
   */
  public scrollToBottomDelayed(delay = 100): void {
    if (!this.scrollContainer || !this.scrollContainer.nativeElement) return;

    setTimeout(() => {
      if (!this.scrollContainer || !this.scrollContainer.nativeElement) return;

      this.isProgrammaticScroll = true;
      scrollToBottom(this.scrollContainer.nativeElement, true);

      // Reset flag after scroll completes
      setTimeout(() => {
        this.isProgrammaticScroll = false;
        this.updateScrollState();
      }, 300);
    }, delay);
  }

  /**
   * Manually enables or disables auto-scroll
   */
  public setAutoScroll(enabled: boolean): void {
    this.scrollStateSubject.next({
      ...this.scrollStateSubject.value,
      autoScroll: enabled,
    });
  }

  /**
   * Resets scroll state to defaults
   */
  public resetScrollState(): void {
    this.isInitialLoad = true;
    this.hasUserInteracted = false;
    this.scrollStateSubject.next({
      showScrollButton: false,
      autoScroll: true,
      lastScrollTop: 0,
      scrolledUp: false,
      scrolledDown: false,
      distanceFromBottom: 0,
      isAtBottom: true,
      isNearBottom: true,
    });
  }

  /**
   * Gets current scroll state snapshot
   */
  public getScrollState(): ChatScrollState {
    return this.scrollStateSubject.value;
  }

  /**
   * Handles new content being added (e.g., new messages)
   * Scrolls to bottom if auto-scroll is enabled
   */
  public handleNewContent(): void {
    // Don't process if we're already in a programmatic scroll
    if (this.isProgrammaticScroll) return;

    const state = this.scrollStateSubject.value;

    // Only scroll if auto-scroll is enabled (user hasn't scrolled up)
    if (state.autoScroll) {
      this.scrollToBottomDelayed(50); // Faster scroll for smoother streaming
    }

    // Mark that user has interacted when new content is added
    // This allows the button to show when they scroll after content is added
    this.hasUserInteracted = true;
  }

  /**
   * Cleanup method - should be called when component is destroyed
   */
  public destroy(): void {
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
      this.scrollSubscription = null;
    }

    // Clean up auto-hide timer
    if (this.autoHideTimer) {
      this.autoHideTimer.unsubscribe();
      this.autoHideTimer = null;
    }

    // Clear DOM references to prevent memory leaks
    this.scrollContainer = null;

    // Reset state to defaults
    this.resetScrollState();
  }

  /**
   * Angular OnDestroy lifecycle hook
   * Automatically cleans up resources when the service is destroyed
   */
  ngOnDestroy(): void {
    this.destroy();
  }
}
